import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { getAllCategories } from "../lib/dynamicCatalog";
import { BoltIcon } from "./Bolts";
import CartButton from "./CartButton";
import { Menu, X, ChevronDown } from "lucide-react";

const navLinkBase =
    "px-3 py-2 text-sm uppercase tracking-[0.18em] font-heading text-white/85 hover:text-storm-bolt transition-colors duration-200 outline-none";

const Header = () => {
    const location = useLocation();
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const categories = getAllCategories(); // builtin + any added via Photo Studio

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <header
            data-testid="site-header"
            className="fixed top-0 inset-x-0 z-[100] bg-storm-void border-b border-white/10 shadow-[0_4px_20px_rgba(0,0,0,0.5)]"
        >
            <div className="container-x flex items-center justify-between h-16 md:h-20">
                <Link
                    to="/"
                    data-testid="logo-link"
                    className="flex items-center gap-2 group"
                >
                    <span className="relative inline-flex items-center justify-center w-9 h-9 bg-storm-bolt text-black animate-bolt-pulse">
                        <BoltIcon className="w-5 h-5" />
                    </span>
                    <span className="font-heading text-xl md:text-2xl tracking-tight">
                        <span className="text-white">DANGER</span>
                        <span className="text-storm-bolt">SKY</span>
                    </span>
                </Link>

                {/* Desktop nav */}
                <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
                    <NavLink to="/" end className={navLinkBase} data-testid="nav-home">
                        Home
                    </NavLink>

                    <div className="flex items-center">
                        {categories.map((cat) => (
                            <div key={cat.slug} className="relative group">
                                <NavLink
                                    to={`/${cat.slug}`}
                                    className={`${navLinkBase} inline-flex items-center gap-1`}
                                    data-testid={`nav-${cat.slug}`}
                                >
                                    {cat.name}
                                    {cat.sub.length > 0 && (
                                        <ChevronDown className="w-3 h-3 opacity-60 group-hover:opacity-100 transition-opacity" />
                                    )}
                                </NavLink>

                                {cat.sub.length > 0 && (
                                    <div className="absolute left-0 top-full pt-2 z-50 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-150">
                                        <div className="bg-storm-navy border border-white/10 w-[320px] p-2 shadow-xl">
                                            <Link
                                                to={`/${cat.slug}`}
                                                data-testid={`nav-${cat.slug}-all`}
                                                className="block px-4 py-3 mb-1 border-b border-white/10 hover:bg-white/5 group/item"
                                            >
                                                <div className="font-heading text-base uppercase tracking-wider text-storm-bolt group-hover/item:text-white transition-colors">
                                                    All {cat.name}
                                                </div>
                                                <div className="text-xs text-storm-mist mt-0.5">
                                                    {cat.tagline}
                                                </div>
                                            </Link>
                                            {cat.sub.map((s) => (
                                                <Link
                                                    key={s.slug}
                                                    to={`/${cat.slug}/${s.slug}`}
                                                    data-testid={`nav-${cat.slug}-${s.slug}`}
                                                    className="block px-4 py-2.5 hover:bg-white/5 transition-colors group/item"
                                                >
                                                    <div className="font-heading text-sm uppercase tracking-wider text-white group-hover/item:text-storm-bolt transition-colors">
                                                        {s.name}
                                                    </div>
                                                    {s.blurb && (
                                                        <div className="text-xs text-storm-mist mt-0.5">
                                                            {s.blurb}
                                                        </div>
                                                    )}
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    <NavLink to="/contact" className={navLinkBase} data-testid="nav-contact">
                        Contact
                    </NavLink>
                </nav>

                <div className="hidden lg:flex items-center gap-3">
                    <CartButton />
                    <a
                        href="#contact-us-now"
                        className="btn-primary !py-3 !px-5 !text-sm"
                        data-testid="header-contact-btn"
                    >
                        Contact Us Now
                    </a>
                </div>

                {/* Mobile */}
                <div className="lg:hidden flex items-center gap-1">
                    <CartButton />
                    <Sheet open={open} onOpenChange={setOpen}>
                        <SheetTrigger asChild>
                            <button
                                data-testid="mobile-menu-trigger"
                                aria-label="Open menu"
                                className="p-2 text-white hover:text-storm-bolt transition-colors"
                            >
                                <Menu className="w-6 h-6" />
                            </button>
                        </SheetTrigger>
                        <SheetContent
                            side="right"
                            className="bg-storm-void border-l border-white/10 text-white w-[88vw] sm:w-[420px] p-0"
                        >
                            <div className="flex items-center justify-between p-5 border-b border-white/10">
                                <span className="font-heading text-xl tracking-tight">
                                    <span className="text-white">DANGER</span>
                                    <span className="text-storm-bolt">SKY</span>
                                </span>
                                <button
                                    onClick={() => setOpen(false)}
                                    className="text-white/70 hover:text-storm-bolt"
                                    aria-label="Close menu"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                            <div className="overflow-y-auto h-[calc(100vh-66px)] p-5 space-y-6">
                                <Link
                                    to="/"
                                    className="block font-heading text-2xl uppercase tracking-wider hover:text-storm-bolt"
                                    data-testid="mobile-nav-home"
                                >
                                    Home
                                </Link>
                                {categories.map((cat) => (
                                    <div key={cat.slug} className="space-y-2">
                                        <Link
                                            to={`/${cat.slug}`}
                                            className="block font-heading text-2xl uppercase tracking-wider hover:text-storm-bolt"
                                            data-testid={`mobile-nav-${cat.slug}`}
                                        >
                                            {cat.name}
                                        </Link>
                                        {cat.sub.length > 0 && (
                                            <ul className="pl-1 border-l border-white/10 ml-1 space-y-1">
                                                {cat.sub.map((s) => (
                                                    <li key={s.slug}>
                                                        <Link
                                                            to={`/${cat.slug}/${s.slug}`}
                                                            data-testid={`mobile-nav-${cat.slug}-${s.slug}`}
                                                            className="block pl-4 py-1 text-sm text-storm-mist hover:text-storm-bolt"
                                                        >
                                                            {s.name}
                                                        </Link>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                ))}
                                <Link
                                    to="/contact"
                                    className="block font-heading text-2xl uppercase tracking-wider hover:text-storm-bolt"
                                    data-testid="mobile-nav-contact"
                                >
                                    Contact
                                </Link>
                                <a
                                    href="#contact-us-now"
                                    onClick={() => setOpen(false)}
                                    className="btn-primary w-full justify-center"
                                    data-testid="mobile-contact-btn"
                                >
                                    Contact Us Now
                                </a>
                            </div>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>
            <div className="lightning-divider" />
        </header>
    );
};

export default Header;
